// Example projects for testing and UI development

import { createProject } from "./project";
import { createLinearInspiredDesignSystem } from "./design-system";
import type { ProjectSummary, ProjectType } from "../types/project-types";

/**
 * Creates a sample SaaS dashboard project for testing
 */
export const createSampleDashboardProject = () => {
  const project = createProject({
    initialProject: {
      metadata: {
        name: "Analytics Dashboard",
        description:
          "A professional analytics dashboard for tracking key business metrics",
        objective:
          "Create a clean, data-focused dashboard that helps teams make informed decisions",
        context:
          "B2B SaaS product targeting product managers and executives who need quick access to KPIs",
        projectType: "dashboard" as ProjectType,
        appLanguages: ["en"],
        targetLanguage: "React",
        tags: ["dashboard", "analytics", "saas", "b2b"],
      },
    },
    autoSave: false,
  });

  return project;
};

/**
 * Creates a sample marketing website project with Linear-inspired design
 */
export const createSampleWebsiteProject = () => {
  const project = createProject({
    initialProject: {
      metadata: {
        name: "TechCorp Landing",
        description: "Modern landing page for a tech startup",
        objective:
          "Convert visitors into trial users with compelling design and clear value proposition",
        context:
          "Tech startup targeting developers and technical decision makers. Clean, professional aesthetic inspired by Linear",
        projectType: "website" as ProjectType,
        appLanguages: ["en", "es"],
        targetLanguage: "TypeScript",
        tags: ["landing-page", "startup", "tech", "linear-inspired"],
      },
    },
    autoSave: false,
  });

  // Apply Linear-inspired design system
  const linearDS = createLinearInspiredDesignSystem(
    "Linear Dark Theme",
    "current-user"
  );

  project.updateDesignSystem({
    baseSystemId: linearDS.id,
    context: "website",
    localOverrides: {
      "colors.primary": "hsl(260, 100%, 80%)", // Bright purple
      "spacing.hero": "contexts.{context}.spacingUnit * 12", // Large hero spacing
    },
    customProperties: {
      "animations.slideIn": "cubic-bezier(0.16, 1, 0.3, 1)",
      "gradients.heroBackground":
        "linear-gradient(135deg, hsl(260, 100%, 8%) 0%, hsl(220, 100%, 12%) 100%)",
    },
  });

  return project;
};

/**
 * Creates a sample mobile app project
 */
export const createSampleAppProject = () => {
  const project = createProject({
    initialProject: {
      metadata: {
        name: "FitTracker Mobile",
        description: "Fitness tracking mobile application",
        objective:
          "Help users track workouts, nutrition, and progress with an intuitive mobile experience",
        context:
          "Consumer mobile app targeting fitness enthusiasts aged 25-45. Needs to work well on iOS and Android",
        projectType: "app" as ProjectType,
        appLanguages: ["en"],
        targetLanguage: "React",
        tags: ["mobile", "fitness", "tracking", "health"],
      },
    },
    autoSave: false,
  });

  // Customize for mobile-specific needs
  project.updateDesignSystem({
    localOverrides: {
      "spacing.touchTarget": "contexts.{context}.spacingUnit * 6", // 48px touch targets
      "fontSizes.mobile": "contexts.{context}.fontSizeBase + 1", // Slightly larger for mobile
    },
    customProperties: {
      "colors.success": "hsl(120, 60%, 50%)",
      "colors.warning": "hsl(45, 100%, 60%)",
      "animations.swipe": "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    },
  });

  return project;
};

/**
 * Gets all example projects for testing
 */
export const getAllExampleProjects = (): {
  name: string;
  project: ReturnType<typeof createProject>;
}[] => [
  {
    name: "Dashboard Example",
    project: createSampleDashboardProject(),
  },
  {
    name: "Website Example",
    project: createSampleWebsiteProject(),
  },
  {
    name: "App Example",
    project: createSampleAppProject(),
  },
];

/**
 * Creates a minimal project for basic testing
 */
export const createMinimalTestProject = () => {
  return createProject({
    initialProject: {
      metadata: {
        name: "Test Project",
        description: "Simple project for testing UI components",
        projectType: "website" as ProjectType,
      },
    },
    autoSave: false,
  });
};

/**
 * Helper to get project summary for UI display
 */
export const getProjectSummary = (
  project: ReturnType<typeof createProject>
): ProjectSummary => {
  const projectData = project.getProject();

  return {
    id: projectData.metadata.id,
    name: projectData.metadata.name,
    description: projectData.metadata.description,
    projectType: projectData.metadata.projectType,
    createdAt: projectData.metadata.created,
    updatedAt: projectData.metadata.modified,
  };
};
